import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import {
  addWeekdayMinimumStayRestriction,
  getWeekdayMinimumStayRulesDataAsync,
  removeWeekdayMinimumStayRestriction,
  selectWeekdayMinimumStayRuleItemsData,
  selectWeekdayMinimumStayRuleItemsDataLoadingStatus,
  updateDefaultRateWeekdayMinimumStayRulesDataAsync,
  updateWeekdayMinimumStayRestriction,
  updateWeekdayMinimumStayRuleItemsData,
} from '../defaultRateWeekdayRulesSlice';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { WeekdayMinimumStayRuleItem } from '../../../common/types/rates.types';
import WeekdayMinimumStayRestrictionsForm from './WeekdayMinimumStayRestrictionsForm';
import { copyWeekdayMinimumStayRulesToSeasons } from '../../defaultRateTools/defaultRateToolsSlice';
import { formatDateToFinnish } from '../../../common/utils/dateUtils';
import SeasonRatePickerContainer from '../../seasonRates/SeasonRatePickerContainer';

function DefaultRateWeekdayMinimumStayRestrictions({
  defaultRateId,
  accommodationId,
  hasWriteAccess,
}: {
  defaultRateId: string;
  accommodationId: string;
  hasWriteAccess: boolean;
}) {
  const dispatch = useAppDispatch();
  const weekdayMinimumStayRuleItemsData = useAppSelector(
    selectWeekdayMinimumStayRuleItemsData,
  );
  const weekdayMinimumStayRuleItemsDataStatus = useAppSelector(
    selectWeekdayMinimumStayRuleItemsDataLoadingStatus,
  );

  const [submitted, setSubmitted] = useState(false);
  const [copyStartDate, setCopyStartDate] = useState<Date | null>(null);
  const [copyEndDate, setCopyEndDate] = useState<Date | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [selectedSeasonRateIds, setSelectedSeasonRateIds] = useState<string[]>(
    [],
  );

  useEffect(() => {
    dispatch(getWeekdayMinimumStayRulesDataAsync(defaultRateId));
  }, [defaultRateId, dispatch]);

  const handleWeekdayMinimumStayRuleChange = (event, weekdayInt) => {
    const { value } = event.target;
    if (value && value > 0) {
      const existingRule =
        weekdayMinimumStayRuleItemsData?.weekdayMinimumDurations?.find(
          rule => rule?.weekday === weekdayInt,
        );

      if (existingRule) {
        const newData = { ...existingRule };
        newData.minimumDurationForCheckIn = value;
        dispatch(updateWeekdayMinimumStayRestriction(newData));
      } else {
        const item = {} as WeekdayMinimumStayRuleItem;
        item.weekday = weekdayInt;
        item.minimumDurationForCheckIn = value;

        dispatch(addWeekdayMinimumStayRestriction(item));
      }
    } else {
      dispatch(removeWeekdayMinimumStayRestriction(weekdayInt));
    }
  };

  const handleUpdateWeekdayMinimumStayRuleItemsData = event => {
    const { checked, name } = event.target;

    const newData = { ...weekdayMinimumStayRuleItemsData };
    newData[name] = checked;
    dispatch(updateWeekdayMinimumStayRuleItemsData(newData));
  };

  const onSubmit = () => {
    dispatch(
      updateDefaultRateWeekdayMinimumStayRulesDataAsync(
        weekdayMinimumStayRuleItemsData,
      ),
    );
    setSubmitted(true);
  };

  const handleCopyingForSeasonRates = async () => {
    if (selectedSeasonRateIds.length > 0) {
      dispatch(
        copyWeekdayMinimumStayRulesToSeasons({
          accommodationId,
          // copyStartDate: formatDateToFinnish(copyStartDate),
          // copyEndDate: formatDateToFinnish(copyEndDate),
          selectedSeasonRateIds,
          weekdayMinimumDurations:
            weekdayMinimumStayRuleItemsData?.weekdayMinimumDurations,
        }),
      )
        .unwrap()
        .then(() => {
          setCopied(true);
        });
    }
  };

  const handleSeasonRateIds = (selectedIds: string[]) => {
    setSelectedSeasonRateIds(selectedIds);
  };

  return (
    <>
      <Row sm={1}>
        <Row sm={1}>
          <Col>
            <Form.Label>Valitse kohdekaudet</Form.Label>
          </Col>
        </Row>
        <Col>
          <SeasonRatePickerContainer
            selectedAccommodationId={accommodationId}
            onSelectedIdsChange={handleSeasonRateIds}
          />
        </Col>
      </Row>
      <WeekdayMinimumStayRestrictionsForm
        loadingStatus={weekdayMinimumStayRuleItemsDataStatus}
        handleMinimumStayInputChange={handleWeekdayMinimumStayRuleChange}
        updateWeekdayMinimumStayRuleItemsData={
          handleUpdateWeekdayMinimumStayRuleItemsData
        }
        onSubmit={onSubmit}
        submitted={submitted}
        data={weekdayMinimumStayRuleItemsData}
        hasWriteAccess={hasWriteAccess}
        isDefaultRate
      />
      <Form.Group className="mb-24">
        {/*
        <Form.Label>Kopioinnin alkupäivämäärä</Form.Label>
        <div className="w-100 position-relative">
          <DatePicker locale={fi}
            dateFormat="dd.MM.yyyy"
            showYearDropdown
            onChange={date => setCopyStartDate(date)}
            className="form-control"
            selected={copyStartDate}
          />
          <Button
            variant="link"
            className="pe-none position-absolute end-0 top-0"
            id="button-addon2"
          >
            <span className="bi bi-calendar2-event" />
          </Button>
        </div>
        <Form.Label>Kopioinnin päättymispäivämäärä</Form.Label>
        <div className="w-100 position-relative">
          <DatePicker locale={fi}
            dateFormat="dd.MM.yyyy"
            showYearDropdown
            onChange={date => setCopyEndDate(date)}
            className="form-control"
            selected={copyEndDate}
          />
          <Button
            variant="link"
            className="pe-none position-absolute end-0 top-0"
            id="button-addon2"
          >
            <span className="bi bi-calendar2-event" />
          </Button>
        </div>
        <Form.Text muted>
          Kohdistuu kausiin joiden alkupäivä on ennen kopioinnin
          päättymispäivämäärää sekä loppupäivä on kopioinnin alkamispäivämäärän
          jälkeen.
        </Form.Text>
        */}
        <Button
          variant="outline-blue"
          className="mt-16 mb-16 w-100"
          onClick={handleCopyingForSeasonRates}
        >
          Kopioi sääntö hintakausille
        </Button>
        {copied && (
          <div className="mt-2 pt-2">
            <Alert variant="success">Sääntö kopioitu hintakausille!</Alert>
          </div>
        )}
      </Form.Group>
    </>
  );
}

export default DefaultRateWeekdayMinimumStayRestrictions;
